import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Shield, CreditCard, FileText, Lock, Truck, Phone } from 'lucide-react';
import Navbar from '../components/Navbar';

// Shared layout for all policy pages
function PolicyLayout({ title, icon: Icon, children }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#f5f5f7]">
      <Navbar />
      
      <div className="pt-20 pb-16">
        {/* Header */}
        <div className="bg-[#1d1d1f] py-12">
          <div className="max-w-4xl mx-auto px-4">
            <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors">
              <ChevronLeft className="w-4 h-4" />
              Back to Home
            </Link>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-[#C41E3A] rounded-xl">
                <Icon className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{title}</h1>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-2xl shadow-sm p-6 sm:p-10">
            {children}
          </div>
          
          {/* Last Updated */}
          <p className="text-center text-zinc-500 text-sm mt-6">
            Last updated: December 2025
          </p>
        </div>
      </div>
    </div>
  );
}

// Reusable section component
function Section({ title, children }) {
  return (
    <div className="mb-8 last:mb-0">
      <h2 className="text-xl font-bold text-[#1d1d1f] mb-4">{title}</h2>
      <div className="text-[#424245] leading-relaxed space-y-3">
        {children}
      </div>
    </div>
  );
}

// ========== TERMS & CONDITIONS ==========
export function TermsAndConditions() {
  return (
    <PolicyLayout title="Terms & Conditions" icon={FileText}>
      <Section title="1. Introduction">
        <p>
          Welcome to MA Africa Fix & Detail. These Terms and Conditions govern your use of our services, 
          including mechanical repairs, panel beating, body repairs, and vehicle detailing services.
        </p>
        <p>
          By booking any service with MA Africa Fix & Detail, you agree to be bound by these Terms and Conditions. 
          Please read them carefully before proceeding with any booking.
        </p>
      </Section>

      <Section title="2. Services">
        <p>MA Africa Fix & Detail provides the following automotive services:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Vehicle Detailing:</strong> Express Wash, Full Valet, Prestige Detail packages</li>
          <li><strong>Mechanical Repairs:</strong> Engine diagnostics, servicing, brake repairs, suspension work</li>
          <li><strong>Panel Beating & Body Repair:</strong> Dent removal, scratch repair, respray, accident damage repair</li>
        </ul>
        <p>
          Service availability, pricing, and turnaround times are subject to vehicle condition assessment 
          and parts availability.
        </p>
      </Section>

      <Section title="3. Booking and Appointments">
        <p>
          Bookings can be made through our website, by phone, or in person at our workshop. 
          All bookings are subject to availability and confirmation by MA Africa Fix & Detail.
        </p>
        <p>
          For certain services, a deposit may be required to secure your booking. Deposits are 
          non-refundable unless the cancellation is made in accordance with our Cancellation Policy.
        </p>
      </Section>

      <Section title="4. Vehicle Inspection">
        <p>
          Upon arrival, your vehicle will undergo a pre-service inspection. This inspection documents 
          the existing condition of your vehicle, including any pre-existing damage. You will be asked 
          to acknowledge this inspection report before work commences.
        </p>
        <p>
          MA Africa Fix & Detail is not responsible for any pre-existing damage that was present 
          before the vehicle was received for service.
        </p>
      </Section>

      <Section title="5. Pricing and Payment">
        <p>
          Prices quoted are estimates based on initial assessment. Final pricing may vary depending on:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Actual condition of the vehicle upon closer inspection</li>
          <li>Additional parts or materials required</li>
          <li>Extended labour time for complex repairs</li>
        </ul>
        <p>
          You will be informed of any significant price changes before additional work is carried out. 
          Payment is due upon completion of services unless otherwise agreed.
        </p>
      </Section>

      <Section title="6. Customer Responsibilities">
        <p>Customers are responsible for:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Providing accurate information about their vehicle and service requirements</li>
          <li>Removing personal belongings from the vehicle before service</li>
          <li>Collecting the vehicle within the agreed timeframe after service completion</li>
          <li>Ensuring the vehicle is legally roadworthy and has valid documentation</li>
        </ul>
      </Section>

      <Section title="7. Warranty">
        <p>
          MA Africa Fix & Detail provides a limited warranty on workmanship for services performed. 
          Warranty periods vary by service type:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Detailing services: 7 days</li>
          <li>Mechanical repairs: 30 days or 5,000 km (whichever comes first)</li>
          <li>Panel beating and paintwork: 12 months</li>
        </ul>
        <p>
          Warranty does not cover damage caused by misuse, accidents, or failure to follow 
          maintenance recommendations.
        </p>
      </Section>

      <Section title="8. Limitation of Liability">
        <p>
          MA Africa Fix & Detail's liability is limited to the value of the services provided. 
          We are not liable for indirect, consequential, or incidental damages arising from our services.
        </p>
        <p>
          We take reasonable care of vehicles in our possession but recommend customers maintain 
          comprehensive vehicle insurance.
        </p>
      </Section>

      <Section title="9. Dispute Resolution">
        <p>
          In the event of any dispute, customers should first contact MA Africa Fix & Detail 
          directly to attempt resolution. If a resolution cannot be reached, disputes will be 
          subject to the jurisdiction of South African courts.
        </p>
      </Section>

      <Section title="10. Changes to Terms">
        <p>
          MA Africa Fix & Detail reserves the right to modify these Terms and Conditions at any time. 
          Updated terms will be posted on our website. Continued use of our services constitutes 
          acceptance of the updated terms.
        </p>
      </Section>

      <Section title="Contact Information">
        <p>For questions regarding these Terms and Conditions, please contact us:</p>
        <div className="bg-zinc-100 rounded-xl p-4 mt-3">
          <p><strong>MA Africa Fix & Detail</strong></p>
          <p>68 Serenade Road, Henville, Germiston</p>
          <p>Phone: 076 078 4050</p>
          <p>Email: info@maafricafixanddetail.co.za</p>
        </div>
      </Section>
    </PolicyLayout>
  );
}

// ========== REFUND & CANCELLATION POLICY ==========
export function RefundPolicy() {
  return (
    <PolicyLayout title="Refund & Cancellation Policy" icon={CreditCard}>
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8">
        <p className="text-amber-800">
          MA Africa Fix & Detail aims to provide transparent and fair cancellation and refund procedures.
        </p>
      </div>

      <Section title="Booking Cancellations">
        <p>
          Customers who wish to cancel or reschedule a booking should contact MA Africa Fix & Detail 
          as soon as reasonably possible.
        </p>
        <p>
          Where a customer cancels before work has commenced, any refund due will be assessed taking 
          into account reasonable costs already incurred in preparing for the service, including 
          specially ordered parts or materials where applicable.
        </p>
      </Section>

      <Section title="Work Already Commenced">
        <p>
          Once mechanical repairs, detailing, panel beating, paintwork or other authorised work has 
          commenced, charges may apply for labour already performed, materials used and parts ordered 
          or installed.
        </p>
        <p>
          Any approved refund will therefore relate only to amounts that remain refundable after 
          reasonable costs already incurred have been deducted.
        </p>
      </Section>

      <Section title="Parts and Special Orders">
        <p>
          Parts or materials specifically ordered for a customer's vehicle may not be refundable 
          where they cannot reasonably be returned to the supplier, subject to applicable South 
          African consumer law.
        </p>
      </Section>

      <Section title="Quality Concerns">
        <p>
          If a customer believes that a service has not been performed as agreed, the customer 
          should contact MA Africa Fix & Detail promptly. The vehicle may need to be inspected 
          before a remedy, corrective work or refund can be considered.
        </p>
      </Section>

      <Section title="Approved Refunds">
        <p>
          Where a refund is approved, it will be processed back through the original payment 
          method where reasonably possible. Processing times may depend on the payment provider 
          and customer's financial institution.
        </p>
        <p className="text-sm text-zinc-500 italic mt-4">
          Nothing in this policy is intended to limit any rights customers may have under 
          applicable South African consumer protection legislation.
        </p>
      </Section>

      <Section title="Cancellation Timeframes">
        <div className="bg-zinc-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-200">
              <tr>
                <th className="text-left p-3 font-semibold">Cancellation Notice</th>
                <th className="text-left p-3 font-semibold">Refund Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-200">
                <td className="p-3">48+ hours before appointment</td>
                <td className="p-3 text-green-600 font-medium">Full deposit refund</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">24-48 hours before appointment</td>
                <td className="p-3 text-amber-600 font-medium">50% deposit refund</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Less than 24 hours / No-show</td>
                <td className="p-3 text-red-600 font-medium">No refund</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>
    </PolicyLayout>
  );
}

// ========== PRIVACY & POPIA POLICY ==========
export function PrivacyPolicy() {
  return (
    <PolicyLayout title="Privacy & POPIA Policy" icon={Lock}>
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
        <p className="text-blue-800">
          This Privacy Policy explains how MA Africa Fix & Detail collects, uses, and protects your 
          personal information in compliance with the Protection of Personal Information Act (POPIA) 
          of South Africa.
        </p>
      </div>

      <Section title="Information We Collect">
        <p>We may collect the following personal information:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Identity Information:</strong> Name, surname, ID number (where required)</li>
          <li><strong>Contact Information:</strong> Phone number, email address, physical address</li>
          <li><strong>Vehicle Information:</strong> Make, model, registration number, VIN</li>
          <li><strong>Transaction Information:</strong> Payment details, service history</li>
          <li><strong>Technical Information:</strong> IP address, browser type (website visitors)</li>
        </ul>
      </Section>

      <Section title="How We Use Your Information">
        <p>Your personal information is used to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Process and manage your service bookings</li>
          <li>Communicate with you about your vehicle and services</li>
          <li>Process payments and issue invoices</li>
          <li>Send service reminders and promotional offers (with your consent)</li>
          <li>Improve our services and customer experience</li>
          <li>Comply with legal and regulatory requirements</li>
        </ul>
      </Section>

      <Section title="Legal Basis for Processing">
        <p>We process your personal information based on:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Consent:</strong> Where you have given us explicit consent</li>
          <li><strong>Contract:</strong> Where processing is necessary to fulfil our service agreement</li>
          <li><strong>Legal Obligation:</strong> Where required by South African law</li>
          <li><strong>Legitimate Interest:</strong> Where necessary for our business operations</li>
        </ul>
      </Section>

      <Section title="Information Sharing">
        <p>We may share your information with:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Payment processors to complete transactions</li>
          <li>Parts suppliers (only vehicle-related information necessary for orders)</li>
          <li>Insurance companies (only with your consent or where legally required)</li>
          <li>Law enforcement or regulatory bodies when legally required</li>
        </ul>
        <p className="mt-3">
          We do not sell your personal information to third parties for marketing purposes.
        </p>
      </Section>

      <Section title="Data Security">
        <p>
          We implement appropriate technical and organisational measures to protect your personal 
          information against unauthorised access, alteration, disclosure, or destruction. These include:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Encrypted data transmission (SSL/TLS)</li>
          <li>Secure payment processing through PCI-compliant providers</li>
          <li>Access controls and staff training</li>
          <li>Regular security assessments</li>
        </ul>
      </Section>

      <Section title="Data Retention">
        <p>
          We retain your personal information only for as long as necessary to fulfil the purposes 
          for which it was collected, or as required by law. Service records may be retained for 
          up to 5 years for warranty and legal purposes.
        </p>
      </Section>

      <Section title="Your Rights Under POPIA">
        <p>You have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li><strong>Access:</strong> Request a copy of your personal information</li>
          <li><strong>Correction:</strong> Request correction of inaccurate information</li>
          <li><strong>Deletion:</strong> Request deletion of your information (subject to legal requirements)</li>
          <li><strong>Object:</strong> Object to processing for direct marketing</li>
          <li><strong>Withdraw Consent:</strong> Withdraw consent where processing is based on consent</li>
          <li><strong>Complaint:</strong> Lodge a complaint with the Information Regulator</li>
        </ul>
      </Section>

      <Section title="Information Officer">
        <p>To exercise your rights or for privacy-related enquiries, contact our Information Officer:</p>
        <div className="bg-zinc-100 rounded-xl p-4 mt-3">
          <p><strong>Information Officer</strong></p>
          <p>MA Africa Fix & Detail</p>
          <p>Email: privacy@maafricafixanddetail.co.za</p>
          <p>Phone: 076 078 4050</p>
        </div>
      </Section>

      <Section title="Cookies">
        <p>
          Our website uses cookies to enhance your browsing experience. Cookies are small text files 
          stored on your device. You can control cookie settings through your browser preferences.
        </p>
      </Section>
    </PolicyLayout>
  );
}

// ========== SERVICE FULFILMENT POLICY ==========
export function ServiceFulfilmentPolicy() {
  return (
    <PolicyLayout title="Service Fulfilment Policy" icon={Truck}>
      <Section title="Service Delivery">
        <p>
          MA Africa Fix & Detail is committed to delivering high-quality automotive services within 
          the agreed timeframes. Our service fulfilment process ensures transparency and customer 
          satisfaction at every step.
        </p>
      </Section>

      <Section title="Booking Confirmation">
        <p>Once your booking is confirmed, you will receive:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>A booking reference number</li>
          <li>Confirmation of the service(s) to be performed</li>
          <li>Estimated completion time</li>
          <li>Price estimate (subject to vehicle inspection)</li>
        </ul>
      </Section>

      <Section title="Vehicle Drop-off">
        <p>When you bring your vehicle to our workshop:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>A pre-service inspection will be conducted in your presence</li>
          <li>Existing damage will be documented and photographed</li>
          <li>You will be asked to sign the inspection report</li>
          <li>We recommend removing all valuables from your vehicle</li>
        </ul>
      </Section>

      <Section title="Service Timeline">
        <div className="bg-zinc-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-200">
              <tr>
                <th className="text-left p-3 font-semibold">Service Type</th>
                <th className="text-left p-3 font-semibold">Typical Duration</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Express Wash</td>
                <td className="p-3">1-2 hours</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Full Valet</td>
                <td className="p-3">3-5 hours</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Prestige Detail</td>
                <td className="p-3">1-2 days</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Minor Mechanical Repairs</td>
                <td className="p-3">Same day - 2 days</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Major Mechanical Repairs</td>
                <td className="p-3">2-7 days (parts dependent)</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Panel Beating / Respray</td>
                <td className="p-3">3-14 days (scope dependent)</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-sm text-zinc-500 mt-3">
          *Actual times may vary based on vehicle condition and parts availability
        </p>
      </Section>

      <Section title="Progress Updates">
        <p>We keep you informed throughout the service process:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>SMS/Email notification when work begins</li>
          <li>Updates if additional work is required (with your approval)</li>
          <li>Notification when your vehicle is ready for collection</li>
          <li>Real-time tracking available through your booking reference</li>
        </ul>
      </Section>

      <Section title="Additional Work">
        <p>
          If during the service we identify additional work that needs to be done, we will:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Contact you to explain the findings</li>
          <li>Provide a revised quote for the additional work</li>
          <li>Only proceed with your explicit approval</li>
          <li>Document any declined recommendations</li>
        </ul>
      </Section>

      <Section title="Vehicle Collection">
        <p>When collecting your vehicle:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Present your booking reference and ID</li>
          <li>Review the completed work with our technician</li>
          <li>Inspect your vehicle before accepting</li>
          <li>Complete payment (if not already paid)</li>
          <li>Receive your invoice and any warranty documentation</li>
        </ul>
      </Section>

      <Section title="Uncollected Vehicles">
        <p>
          Vehicles should be collected within 48 hours of completion notification. Vehicles not 
          collected within 7 days may incur storage fees of R150 per day. MA Africa Fix & Detail 
          reserves the right to exercise a lien over uncollected vehicles.
        </p>
      </Section>
    </PolicyLayout>
  );
}

// ========== PAYMENT INFORMATION ==========
export function PaymentInformation() {
  return (
    <PolicyLayout title="Payment Information" icon={CreditCard}>
      <Section title="How Payments Work">
        <ol className="list-decimal pl-6 space-y-3">
          <li>Select the automotive service you require or request a quotation through our website.</li>
          <li>MA Africa Fix & Detail will confirm your booking, service requirements and applicable price.</li>
          <li>Where a deposit or full payment is required, the customer will be provided with a secure online payment option.</li>
          <li>Online payments are processed securely through our approved payment provider.</li>
          <li>Once payment is successful, the customer receives confirmation and the vehicle is scheduled for the agreed service.</li>
          <li>For mechanical repairs and panel beating where the final cost depends on an inspection or assessment, customers will receive a quotation before payment is requested.</li>
        </ol>
        <p className="mt-4 text-sm text-zinc-500">
          MA Africa Fix & Detail does not store customers' card details on its own servers.
        </p>
      </Section>

      <Section title="Accepted Payment Methods">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
          <div className="bg-zinc-100 rounded-xl p-4 text-center">
            <CreditCard className="w-8 h-8 text-[#C41E3A] mx-auto mb-2" />
            <p className="text-sm font-medium">Credit Card</p>
            <p className="text-xs text-zinc-500">Visa, Mastercard</p>
          </div>
          <div className="bg-zinc-100 rounded-xl p-4 text-center">
            <CreditCard className="w-8 h-8 text-[#C41E3A] mx-auto mb-2" />
            <p className="text-sm font-medium">Debit Card</p>
            <p className="text-xs text-zinc-500">Visa, Mastercard</p>
          </div>
          <div className="bg-zinc-100 rounded-xl p-4 text-center">
            <svg className="w-8 h-8 text-[#C41E3A] mx-auto mb-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.91s4.18 1.39 4.18 3.91c-.01 1.83-1.38 2.83-3.12 3.16z"/>
            </svg>
            <p className="text-sm font-medium">Cash</p>
            <p className="text-xs text-zinc-500">At workshop</p>
          </div>
          <div className="bg-zinc-100 rounded-xl p-4 text-center">
            <svg className="w-8 h-8 text-[#C41E3A] mx-auto mb-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
            </svg>
            <p className="text-sm font-medium">EFT</p>
            <p className="text-xs text-zinc-500">Bank Transfer</p>
          </div>
        </div>
      </Section>

      <Section title="Deposit Requirements">
        <div className="bg-zinc-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-zinc-200">
              <tr>
                <th className="text-left p-3 font-semibold">Service Type</th>
                <th className="text-left p-3 font-semibold">Deposit Required</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Detailing Services</td>
                <td className="p-3">50% deposit on booking</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Mechanical Repairs</td>
                <td className="p-3">Parts cost upfront (if applicable)</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Panel Beating / Respray</td>
                <td className="p-3">30-50% deposit on approval</td>
              </tr>
              <tr className="border-t border-zinc-200">
                <td className="p-3">Fleet Services</td>
                <td className="p-3">Custom terms (contact us)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Payment Security">
        <p>Your payment security is our priority:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>All online payments are processed through Paystack, a PCI-DSS compliant payment provider</li>
          <li>Your card details are encrypted using 256-bit SSL encryption</li>
          <li>We never store your full card number on our servers</li>
          <li>3D Secure authentication is enabled for additional protection</li>
        </ul>
      </Section>

      <Section title="Invoicing">
        <p>
          Upon completion of services, you will receive a detailed invoice showing:
        </p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Itemised list of services performed</li>
          <li>Parts and materials used (if applicable)</li>
          <li>Labour charges</li>
          <li>VAT breakdown</li>
          <li>Total amount and payment status</li>
        </ul>
      </Section>

      <Section title="Questions About Payment">
        <p>For any payment-related enquiries, please contact us:</p>
        <div className="bg-zinc-100 rounded-xl p-4 mt-3">
          <p><strong>MA Africa Fix & Detail</strong></p>
          <p>Phone: 076 078 4050</p>
          <p>Email: accounts@maafricafixanddetail.co.za</p>
        </div>
      </Section>
    </PolicyLayout>
  );
}

// ========== CONTACT US ==========
export function ContactUsPage() {
  return (
    <PolicyLayout title="Contact Us" icon={Phone}>
      <Section title="Get In Touch">
        <p>
          We'd love to hear from you! Whether you have questions about our services, need a quote, 
          or want to book an appointment, our team is here to help.
        </p>
      </Section>

      <div className="grid sm:grid-cols-2 gap-6 my-8">
        <div className="bg-zinc-100 rounded-xl p-6">
          <h3 className="font-bold text-[#1d1d1f] mb-4">Workshop Location</h3>
          <p className="text-[#424245]">
            68 Serenade Road<br />
            Henville, Germiston<br />
            Gauteng, South Africa
          </p>
          <a 
            href="https://maps.google.com/?q=68+Serenade+Road+Henville+Germiston" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-[#C41E3A] font-medium mt-4 hover:underline"
          >
            Get Directions →
          </a>
        </div>

        <div className="bg-zinc-100 rounded-xl p-6">
          <h3 className="font-bold text-[#1d1d1f] mb-4">Contact Details</h3>
          <div className="space-y-3 text-[#424245]">
            <p>
              <strong>Phone:</strong><br />
              <a href="tel:0760784050" className="text-[#C41E3A] hover:underline">076 078 4050</a>
            </p>
            <p>
              <strong>Email:</strong><br />
              <a href="mailto:info@maafricafixanddetail.co.za" className="text-[#C41E3A] hover:underline">
                info@maafricafixanddetail.co.za
              </a>
            </p>
            <p>
              <strong>WhatsApp:</strong><br />
              <a href="https://wa.me/27760784050" className="text-[#C41E3A] hover:underline">
                Chat with us
              </a>
            </p>
          </div>
        </div>
      </div>

      <Section title="Business Hours">
        <div className="bg-zinc-100 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <tbody>
              <tr className="border-b border-zinc-200">
                <td className="p-3 font-medium">Monday - Friday</td>
                <td className="p-3 text-right">07:00 - 18:00</td>
              </tr>
              <tr className="border-b border-zinc-200">
                <td className="p-3 font-medium">Saturday</td>
                <td className="p-3 text-right">08:00 - 15:00</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Sunday & Public Holidays</td>
                <td className="p-3 text-right text-zinc-500">Closed</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>

      <Section title="Quick Links">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <a href="/booking" className="bg-[#C41E3A] text-white rounded-xl p-4 text-center hover:bg-[#a01830] transition-colors">
            Book a Service
          </a>
          <a href="/quote" className="bg-zinc-800 text-white rounded-xl p-4 text-center hover:bg-zinc-700 transition-colors">
            Request a Quote
          </a>
          <a href="/track" className="bg-zinc-200 text-zinc-800 rounded-xl p-4 text-center hover:bg-zinc-300 transition-colors">
            Track Your Service
          </a>
        </div>
      </Section>
    </PolicyLayout>
  );
}

export default TermsAndConditions;
